import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { playCorrectDing } from "@/lib/sfx";
import { playVoice } from "@/lib/tts";

// Grades 1-3: every 5 questions a star floats across the screen; tapping it gives effort points.
let lastStar = 0;

export default function StarCatch({ show, childId, lang }) {
  const [state, setState] = useState("idle");
  const [pts, setPts] = useState(0);
  const caughtRef = useRef(0);
  const fr = lang === "fr";
  useEffect(() => {
    if (!show || show === lastStar) return undefined;
    lastStar = show;
    setState("fly");
    const t = setTimeout(() => setState((s) => (s === "fly" ? "idle" : s)), 9000);
    return () => clearTimeout(t);
  }, [show]);

  const catchIt = async () => {
    if (state !== "fly" || caughtRef.current === show) return;
    caughtRef.current = show;
    setState("caught");
    playCorrectDing();
    let got = 0;
    try { const { data } = await api.post(`/children/${childId}/star-catch`); got = data.awarded; setPts(got); } catch { setPts(0); }
    if (localStorage.getItem("aa_voice_on") !== "off") {
      playVoice(fr ? (got ? `Bravo ! Tu as attrapé l'étoile ! Plus ${got} points !` : "Bravo ! Tu as attrapé l'étoile !")
        : (got ? `Well done! You caught the star! Plus ${got} points!` : "Well done! You caught the star!"), fr ? "fr" : "en");
    }
    setTimeout(() => setState("idle"), 1800);
  };

  return createPortal(
    <AnimatePresence>
      {state === "fly" && (
        <motion.button key={`fly-${show}`} onPointerDown={catchIt} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && catchIt()} data-testid="star-catch-btn" aria-label={fr ? "Attrape l'étoile" : "Catch the star"}
          className="fixed z-[80] w-36 h-36 -ml-[4.5rem] rounded-full flex items-center justify-center cursor-pointer touch-manipulation"
          initial={{ left: "-20vw", top: "30%" }}
          animate={{ left: ["-20vw", "50vw", "50vw", "54vw", "50vw", "115vw"], top: ["30%", "28%", "34%", "28%", "32%", "22%"], scale: [1, 1, 1.15, 1, 1.15, 1] }}
          transition={{ duration: 9, times: [0, 0.2, 0.4, 0.6, 0.8, 1], ease: "easeInOut" }} exit={{ scale: 0, opacity: 0, transition: { duration: 0.2 } }}>
          <span className="absolute inset-4 rounded-full bg-yellow-200/60 animate-ping" />
          <Star size={96} className="relative text-yellow-400 drop-shadow-[0_0_18px_rgba(250,204,21,0.95)]" fill="currentColor" strokeWidth={1.5} />
          <span className="absolute -bottom-7 whitespace-nowrap font-kids font-bold text-lg text-slate-900 bg-white rounded-full px-3 border-2 border-slate-900">
            {fr ? "Touche-moi !" : "Tap me!"}
          </span>
        </motion.button>
      )}
      {state === "caught" && (
        <motion.div key="caught" className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center" data-testid="star-catch-done"
          initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
          <div className="rounded-3xl border-4 border-slate-900 bg-yellow-300 px-8 py-5 font-kids font-bold text-3xl text-slate-900 shadow-[6px_6px_0_0_#0F172A] flex items-center gap-3">
            <Star size={40} fill="currentColor" /> {fr ? "Bravo !" : "Got it!"} {pts > 0 && <span data-testid="star-catch-points">+{pts} {fr ? "points d'effort" : "effort points"}</span>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
